import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Globe2,
  RefreshCw,
  Rocket,
  TicketCheck,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "../../design/components/Button.js";
import { Alert, Skeleton } from "../../design/components/Feedback.js";
import { PageHeader } from "../../design/components/PageHeader.js";
import { StatusBadge } from "../../design/components/StatusBadge.js";
import { api, type Conference, type LaunchCheck, type LaunchReadiness } from "../api.js";
import { formatDateRange } from "../formatters.js";

const journeyIcon = {
  public_site: Globe2,
  author: FileText,
  reviewer: ClipboardCheck,
  attendee: TicketCheck,
} as const;

function checkTone(status: LaunchCheck["status"]) {
  if (status === "READY") return "success" as const;
  if (status === "BLOCKED") return "danger" as const;
  if (status === "ACTION") return "info" as const;
  return "neutral" as const;
}

function checkLabel(status: LaunchCheck["status"]) {
  if (status === "READY") return "Ready";
  if (status === "BLOCKED") return "Blocked";
  if (status === "ACTION") return "Check";
  return "Optional";
}

export function ConferenceHome({ conference }: { conference: Conference }) {
  const [readiness, setReadiness] = useState<LaunchReadiness | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setReadiness(await api.launchReadiness(conference.id, conference.organization_id));
    } catch {
      setReadiness(null);
      setError("We could not run the launch checks. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [conference.id, conference.organization_id]);

  useEffect(() => { void load(); }, [load]);

  const summary = readiness?.summary;
  const percent = summary ? Math.round((summary.required_ready / Math.max(summary.required_total, 1)) * 100) : 0;
  const requiredChecks = readiness?.checks.filter((check) => check.group === "REQUIRED") ?? [];
  const operationsChecks = readiness?.checks.filter((check) => check.group === "OPERATIONS") ?? [];

  return <>
    <PageHeader
      title={conference.name}
      description="Run launch checks, resolve real blockers, and test every participant entry point from one place."
      actions={<Button variant="secondary" busy={loading} onClick={() => void load()}><RefreshCw size={16} aria-hidden="true" />Run launch checks</Button>}
    />
    <section className="conference-facts" aria-label="Conference details">
      <article><span>Visibility</span><strong>{conference.is_public ? "Public" : "Private"}</strong></article>
      <article><span>Dates</span><strong>{formatDateRange(conference.starts_on, conference.ends_on)}</strong></article>
      <article><span>Time zone</span><strong>{conference.time_zone}</strong></article>
    </section>

    {loading && !readiness ? <section className="launch-loading" aria-label="Running launch checks"><Skeleton /><Skeleton /><Skeleton /></section> : null}
    {error ? <Alert tone="danger">{error} <Button variant="secondary" onClick={() => void load()}>Try again</Button></Alert> : null}

    {readiness && summary ? <div className="launch-centre">
      <section className={`launch-summary${summary.launch_ready ? " is-ready" : ""}`}>
        <div className="launch-summary__icon"><Rocket size={25} aria-hidden="true" /></div>
        <div className="launch-summary__copy">
          <span>Conference launch</span>
          <h2>{summary.launch_ready ? "Ready for participant testing" : `${summary.blocker_count} launch blocker${summary.blocker_count === 1 ? "" : "s"}`}</h2>
          <p>{summary.launch_ready ? "Every required launch gate is ready. Optional event-delivery surfaces remain independently controlled." : "Resolve the required items below before broadly sharing participant links."}</p>
        </div>
        <div className="launch-progress" aria-label={`${summary.required_ready} of ${summary.required_total} required checks ready`}>
          <div><span>Required readiness</span><strong>{percent}%</strong></div>
          <span className="launch-progress__track"><span style={{ width: `${percent}%` }} /></span>
          <small>{summary.required_ready} of {summary.required_total} required checks ready</small>
        </div>
      </section>

      <section className="launch-checks" aria-label="Launch readiness checks">
        <header><div><span>Required launch gates</span><h2>Ready to share</h2></div><p>These checks protect the public, author, and delivery journey. Each control remains independently published or opened.</p></header>
        <div className="launch-check-grid">{requiredChecks.map((check) => <article className={`launch-check is-${check.status.toLowerCase()}`} key={check.key}>
          <header><StatusBadge tone={checkTone(check.status)}>{checkLabel(check.status)}</StatusBadge>{check.status === "READY" ? <CheckCircle2 size={19} aria-hidden="true" /> : <AlertTriangle size={19} aria-hidden="true" />}</header>
          <h3>{check.label}</h3><p>{check.detail}</p><Link to={check.action.href}>{check.action.label}</Link>
        </article>)}</div>
      </section>

      <section className="launch-checks" aria-label="Operational launch checks">
        <header><div><span>Operational readiness</span><h2>Complete the event experience</h2></div><p>These surfaces can be introduced when they fit the conference. They never open automatically.</p></header>
        <div className="launch-check-grid">{operationsChecks.map((check) => <article className={`launch-check is-${check.status.toLowerCase()}`} key={check.key}>
          <header><StatusBadge tone={checkTone(check.status)}>{checkLabel(check.status)}</StatusBadge>{check.status === "READY" ? <CheckCircle2 size={19} aria-hidden="true" /> : <AlertTriangle size={19} aria-hidden="true" />}</header>
          <h3>{check.label}</h3><p>{check.detail}</p><Link to={check.action.href}>{check.action.label}</Link>
        </article>)}</div>
      </section>

      <section className="launch-journeys" aria-label="Participant test journeys">
        <header><div><span>Test before sharing</span><h2>Walk every participant path</h2></div><p>Available tests use the real conference links and current permissions. Personal invitation boundaries are preserved.</p></header>
        <div>{readiness.test_journeys.map((journey) => {
          const Icon = journeyIcon[journey.key];
          return <article className={journey.available ? "is-available" : ""} key={journey.key}>
            <Icon size={21} aria-hidden="true" />
            <div><h3>{journey.label}</h3><p>{journey.detail}</p></div>
            {journey.available && journey.path ? <Link to={journey.path} target="_blank" rel="noreferrer">Test {journey.label.toLocaleLowerCase()}</Link> : <button type="button" aria-label={`${journey.label} unavailable`} disabled>Unavailable</button>}
          </article>;
        })}</div>
      </section>
    </div> : null}
  </>;
}
